import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FileSearch, Loader2, Copy, Send, CheckCircle2, XCircle, History, ClipboardCheck, UserPlus } from "lucide-react";

const STATUTS = {
  brouillon: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  transmise: { label: "Transmise", cls: "bg-blue-100 text-blue-700" },
  en_etude: { label: "En étude", cls: "bg-amber-100 text-amber-800" },
  proposition_recue: { label: "Proposition reçue", cls: "bg-violet-100 text-violet-800" },
  a_ajuster: { label: "À ajuster", cls: "bg-orange-100 text-orange-800" },
  acceptee: { label: "Acceptée", cls: "bg-emerald-100 text-emerald-800" },
  refusee: { label: "Refusée", cls: "bg-red-100 text-red-700" },
  annulee: { label: "Annulée", cls: "bg-slate-200 text-slate-500" },
  affectations_preparees: { label: "Affectations préparées", cls: "bg-teal-100 text-teal-800" },
};
const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const EMPTY_LIGNE = { profil: "", qualification: "", nombre: 1 };
const EMPTY = {
  entreprise_id: "", lignes: [{ ...EMPTY_LIGNE }], site_intervention: "", responsable_nom: "",
  responsable_email: "", responsable_tel: "", date_debut: "", date_fin: "",
  jours_travailles: [], horaires: "", pauses: "", competences: "", habilitations: "",
  contraintes_poste: "", equipements: "", consignes: "", deplacements_prevus: "", frais_prevus: "", description: "",
};

export default function Demandes() {
  const { user } = useAuth();
  const isGE = ["admin", "gestionnaire_ge"].includes(user?.role);
  const isEntreprise = user?.role === "employeur";
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [propositions, setPropositions] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [propForm, setPropForm] = useState({ tarif_horaire: "", validite_jours: 15, frais_prevus: "", commentaire: "", profils: [] });
  const [decisionForm, setDecisionForm] = useState({ decision: "acceptee", commentaire: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/demandes"), api.get("/entreprises")];
      if (isGE) reqs.push(api.get("/salaries"));
      const [a, b, c] = await Promise.all(reqs);
      setItems(a.data); setEntreprises(b.data);
      if (isGE && c) setSalaries(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isGE]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isEntreprise && entreprises.length === 1 && !form.entreprise_id) {
      setForm(f => ({ ...f, entreprise_id: entreprises[0].id }));
    }
  }, [entreprises, isEntreprise, form.entreprise_id]);

  const openDetail = async (d) => {
    try {
      const full = d.version_schema === 2 ? (await api.get(`/demandes/v2/${d.id}`)).data : d;
      setDetail(full);
      const pr = await api.get(`/demandes/v2/${d.id}/propositions`).catch(() => ({ data: [] }));
      setPropositions(pr.data || []);
      const cd = await api.get(`/demandes/v2/${d.id}/conditions-demarrage`).catch(() => ({ data: null }));
      setConditions(cd.data);
      setPropForm({ tarif_horaire: "", validite_jours: 15, frais_prevus: "", commentaire: "", profils: [] });
      setDecisionForm({ decision: "acceptee", commentaire: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async (transmettre) => {
    try {
      const payload = { ...form, competences: form.competences ? form.competences.split(",").map(s => s.trim()).filter(Boolean) : [],
        habilitations: form.habilitations ? form.habilitations.split(",").map(s => s.trim()).filter(Boolean) : [] };
      Object.keys(payload).forEach(k => { if (payload[k] === "" || (Array.isArray(payload[k]) && payload[k].length === 0 && k !== "lignes")) delete payload[k]; });
      payload.lignes = payload.lignes.filter(l => l.profil.trim()).map(l => ({ ...l, nombre: Number(l.nombre) || 1 }));
      const r = await api.post("/demandes/v2", payload);
      const did = r.data.id;
      if (transmettre) {
        await api.post(`/demandes/v2/${did}/transition`, { statut_cible: "transmise" });
        toast.success("Demande transmise au groupement");
      } else {
        toast.success("Brouillon enregistré");
      }
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };

  const transition = async (did, statut, commentaire) => {
    try {
      await api.post(`/demandes/v2/${did}/transition`, { statut_cible: statut, commentaire });
      toast.success(`Statut : ${STATUTS[statut]?.label}`); load();
      if (detail?.id === did) openDetail(detail);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dupliquer = async (did) => {
    try { await api.post(`/demandes/v2/${did}/dupliquer`); toast.success("Demande dupliquée en brouillon"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const envoyerProposition = async () => {
    try {
      const profils = detail.lignes.flatMap(l =>
        propForm.profils.filter(p => p.ligne_profil === l.profil && p.salarie_id)
          .map(p => ({ ligne_profil: p.ligne_profil, salarie_id: p.salarie_id, disponibilite_verifiee: true }))
      );
      const lignes_sans = detail.lignes.filter(l => !propForm.profils.some(p => p.ligne_profil === l.profil && p.salarie_id));
      lignes_sans.forEach(l => profils.push({ ligne_profil: l.profil, profil_libre: "Profil à identifier", disponibilite_verifiee: false }));
      const r = await api.post(`/demandes/v2/${detail.id}/propositions`, {
        profils,
        tarif_horaire: propForm.tarif_horaire ? Number(propForm.tarif_horaire) : undefined,
        validite_jours: Number(propForm.validite_jours),
        frais_prevus: propForm.frais_prevus || undefined,
        commentaire: propForm.commentaire || undefined,
      });
      toast.success(`Proposition v${r.data.version} envoyée à l'entreprise`);
      openDetail(detail); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deciderProposition = async (pid) => {
    try {
      await api.post(`/propositions/${pid}/decision`, decisionForm);
      toast.success(`Proposition ${decisionForm.decision === "acceptee" ? "acceptée" : decisionForm.decision === "refusee" ? "refusée" : "renvoyée pour ajustement"}`);
      openDetail(detail); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const preparerAffectations = async (pid) => {
    try {
      const r = await api.post(`/propositions/${pid}/prepare-affectations`);
      toast.success(`${r.data.affectations.length} affectation(s) + ${r.data.conventions.length} convention(s) préparée(s)`);
      openDetail(detail); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : formatApiError(det));
    }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";
  const demandesV2 = items.filter(d => d.version_schema === 2);
  const demandesLegacy = items.filter(d => d.version_schema !== 2);

  return (
    <div data-testid="demandes-page">
      <PageHeader
        title="Demandes de personnel"
        subtitle="Brouillon → transmise → étude GE → proposition → acceptation → affectations préparées."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="demande-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle demande
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="dem-new-desc">
              <DialogHeader>
                <DialogTitle>Demande de personnel guidée</DialogTitle>
                <DialogDescription id="dem-new-desc">Multi-profils. L'entreprise et ses coordonnées connues sont préremplies côté serveur.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Entreprise utilisatrice *</Label>
                  <Select value={form.entreprise_id} onValueChange={(v) => setForm({ ...form, entreprise_id: v })} disabled={isEntreprise && entreprises.length === 1}>
                    <SelectTrigger data-testid="dem-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e => <SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Profils demandés *</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, lignes: [...form.lignes, { ...EMPTY_LIGNE }] })} data-testid="dem-add-ligne">+ Profil</Button>
                  </div>
                  {form.lignes.map((l, i) => (
                    <div key={i} className="flex gap-2 items-end" data-testid={`dem-ligne-${i}`}>
                      <Input className="flex-1" placeholder="Métier / profil *" value={l.profil} onChange={e => setForm({ ...form, lignes: form.lignes.map((x, j) => j === i ? { ...x, profil: e.target.value } : x) })} data-testid={`dem-profil-${i}`} />
                      <Input className="w-40" placeholder="Qualification" value={l.qualification} onChange={e => setForm({ ...form, lignes: form.lignes.map((x, j) => j === i ? { ...x, qualification: e.target.value } : x) })} />
                      <Input type="number" min={1} className="w-20" title="Nombre de personnes" value={l.nombre} onChange={e => setForm({ ...form, lignes: form.lignes.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x) })} data-testid={`dem-nombre-${i}`} />
                      {form.lignes.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, lignes: form.lignes.filter((_, j) => j !== i) })}>×</Button>}
                    </div>
                  ))}
                </div>
                <div className="space-y-2"><Label>Date début *</Label><Input type="date" value={form.date_debut} onChange={e => setForm({ ...form, date_debut: e.target.value })} data-testid="dem-date-debut" /></div>
                <div className="space-y-2"><Label>Date fin *</Label><Input type="date" value={form.date_fin} onChange={e => setForm({ ...form, date_fin: e.target.value })} data-testid="dem-date-fin" /></div>
                <div className="space-y-2"><Label>Site d'intervention</Label><Input value={form.site_intervention} onChange={e => setForm({ ...form, site_intervention: e.target.value })} placeholder="Prérempli avec l'adresse de l'entreprise si vide" /></div>
                <div className="space-y-2"><Label>Horaires</Label><Input value={form.horaires} onChange={e => setForm({ ...form, horaires: e.target.value })} placeholder="08:00-12:00 / 13:00-17:00" /></div>
                <div className="space-y-2"><Label>Pauses</Label><Input value={form.pauses} onChange={e => setForm({ ...form, pauses: e.target.value })} placeholder="12:00-13:00" /></div>
                <div className="space-y-2"><Label>Responsable opérationnel</Label><Input value={form.responsable_nom} onChange={e => setForm({ ...form, responsable_nom: e.target.value })} placeholder="Prérempli avec le dirigeant si vide" /></div>
                <div className="space-y-2 col-span-2">
                  <Label>Jours travaillés</Label>
                  <div className="flex gap-1 flex-wrap">
                    {JOURS.map(j => (
                      <Button key={j} type="button" size="sm" variant={form.jours_travailles.includes(j) ? "default" : "outline"}
                        className={form.jours_travailles.includes(j) ? "bg-[#1B2A4A] text-white" : ""}
                        onClick={() => setForm({ ...form, jours_travailles: form.jours_travailles.includes(j) ? form.jours_travailles.filter(x => x !== j) : [...form.jours_travailles, j] })}
                        data-testid={`dem-jour-${j}`}>{j.slice(0, 3)}</Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 col-span-2"><Label>Compétences / habilitations (séparées par des virgules)</Label><Input value={form.competences} onChange={e => setForm({ ...form, competences: e.target.value })} placeholder="accueil, caisse, CACES…" /></div>
                <div className="space-y-2 col-span-2"><Label>Contraintes du poste, équipements, consignes</Label><Textarea rows={2} value={form.contraintes_poste} onChange={e => setForm({ ...form, contraintes_poste: e.target.value })} /></div>
                <div className="space-y-2"><Label>Déplacements prévus</Label><Input value={form.deplacements_prevus} onChange={e => setForm({ ...form, deplacements_prevus: e.target.value })} /></div>
                <div className="space-y-2"><Label>Frais prévus</Label><Input value={form.frais_prevus} onChange={e => setForm({ ...form, frais_prevus: e.target.value })} placeholder="repas, km…" /></div>
                <div className="space-y-2 col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="dem-description" /></div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => submit(false)} data-testid="dem-save-draft">Enregistrer en brouillon</Button>
                <Button onClick={() => submit(true)} data-testid="dem-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Send className="h-4 w-4 mr-1" /> Transmettre au groupement
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
        items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="demandes-empty">
            <FileSearch className="h-10 w-10 mx-auto text-slate-300" /><p className="mt-3 text-slate-500">Aucune demande en cours.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="demandes-list">
            {[...demandesV2, ...demandesLegacy].map(d => {
              const st = STATUTS[d.statut] || { label: d.statut, cls: "bg-slate-100" };
              const lignes = d.lignes || (d.profil_recherche ? [{ profil: d.profil_recherche, nombre: 1 }] : []);
              return (
                <div key={d.id} className="kpi-card bg-white border border-slate-200 rounded-xl p-5" data-testid={`demande-card-${d.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{entLabel(d.entreprise_id)}</div>
                      <h3 className="mt-1 text-base font-semibold text-slate-900">
                        {lignes.map(l => `${l.nombre}× ${l.profil}`).join(" · ") || d.profil_recherche || "Demande"}
                      </h3>
                      <div className="text-xs text-slate-500 mt-1">{d.date_debut || d.date_debut_souhaitee} → {d.date_fin || d.date_fin_souhaitee}</div>
                    </div>
                    <Badge className={st.cls} data-testid={`demande-statut-${d.id}`}>{st.label}</Badge>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openDetail(d)} data-testid={`demande-open-${d.id}`}>Ouvrir le dossier</Button>
                    <Button size="sm" variant="ghost" onClick={() => dupliquer(d.id)} data-testid={`demande-dup-${d.id}`}><Copy className="h-3.5 w-3.5 mr-1" />Dupliquer</Button>
                    {isEntreprise && d.statut === "brouillon" && (
                      <Button size="sm" onClick={() => transition(d.id, "transmise")} data-testid={`demande-transmettre-${d.id}`} className="bg-[#1B2A4A] text-white"><Send className="h-3.5 w-3.5 mr-1" />Transmettre</Button>
                    )}
                    {isGE && d.statut === "transmise" && (
                      <Button size="sm" onClick={() => transition(d.id, "en_etude")} data-testid={`demande-etude-${d.id}`} className="bg-[#1B2A4A] text-white">Prendre en étude</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={v => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="demande-detail-dialog" aria-describedby="demande-detail-desc">
          <DialogHeader>
            <DialogTitle>Dossier demande — {detail?.entreprise_raison_sociale || entLabel(detail?.entreprise_id)}</DialogTitle>
            <DialogDescription id="demande-detail-desc">Demande → proposition → acceptation → affectations. Un seul dossier d'opération.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <div className="flex items-center gap-2">
                <Badge className={STATUTS[detail.statut]?.cls} data-testid="demande-detail-statut">{STATUTS[detail.statut]?.label}</Badge>
                <span className="text-xs text-slate-500">{detail.date_debut} → {detail.date_fin} · {detail.horaires || "horaires non précisés"}</span>
              </div>

              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Profils demandés</div>
                {(detail.lignes || []).map((l, i) => (
                  <div key={i} className="text-sm">{l.nombre}× <strong>{l.profil}</strong>{l.qualification ? ` (${l.qualification})` : ""}</div>
                ))}
                {detail.competences?.length > 0 && <div className="text-xs text-slate-600 mt-1">Compétences : {detail.competences.join(", ")}</div>}
              </div>

              {(detail.historique || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-500 font-semibold mb-1 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Historique des transitions</div>
                  <div className="space-y-1 text-xs" data-testid="demande-historique">
                    {detail.historique.map((h, i) => (
                      <div key={i} className="bg-slate-50 rounded px-2 py-1">
                        {h.at?.slice(0, 16).replace("T", " ")} — <strong>{STATUTS[h.statut]?.label || h.statut}</strong> par {h.par}
                        {h.prochaine_action && <span className="text-slate-500"> → {h.prochaine_action}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Propositions du groupement ({propositions.length})</div>
                {propositions.length === 0 && <p className="text-xs text-slate-400 italic">Aucune proposition pour l'instant.</p>}
                {propositions.map(p => (
                  <div key={p.id} className="border rounded-lg p-3 mb-2" data-testid={`proposition-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge variant="outline">v{p.version}</Badge>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${p.statut === "acceptee" ? "bg-emerald-100 text-emerald-800" : p.statut === "envoyee" ? "bg-blue-100 text-blue-700" : "bg-slate-100"}`}>{p.statut}</span>
                      </div>
                      <div className="text-xs text-slate-500">valide jusqu'au {p.date_expiration}</div>
                    </div>
                    <div className="mt-2 text-xs space-y-0.5">
                      {p.tarif_horaire && <div>Tarif : <strong>{p.tarif_horaire} €/h</strong>{p.cout_previsionnel_ht ? ` · coût prévisionnel ${Number(p.cout_previsionnel_ht).toLocaleString("fr-FR")} € HT` : ""}</div>}
                      {(p.profils || []).map((pp, i) => (
                        <div key={i}>
                          • {pp.ligne_profil} : {pp.salarie ? `${pp.salarie.prenom} ${pp.salarie.nom} (${pp.salarie.poste || "—"})` : (pp.salarie_id ? "salarié désigné" : pp.profil_libre || "à identifier")}
                          {pp.disponibilite_verifiee ? " — disponibilité vérifiée" : ""}
                        </div>
                      ))}
                      {p.frais_prevus && <div>Frais prévus : {p.frais_prevus}</div>}
                    </div>
                    {isEntreprise && p.statut === "envoyee" && (
                      <div className="mt-3 border-t pt-3 space-y-2">
                        <div className="flex gap-2">
                          {[["acceptee", "Accepter", CheckCircle2, "bg-emerald-600"], ["a_ajuster", "Demander un ajustement", null, "bg-amber-500"], ["refusee", "Refuser", XCircle, "bg-red-600"]].map(([k, lbl, Icon, cls]) => (
                            <Button key={k} size="sm" variant={decisionForm.decision === k ? "default" : "outline"}
                              className={decisionForm.decision === k ? `${cls} text-white` : ""}
                              onClick={() => setDecisionForm({ ...decisionForm, decision: k })} data-testid={`prop-decision-${p.id}-${k}`}>{lbl}</Button>
                          ))}
                        </div>
                        <Textarea rows={2} placeholder={decisionForm.decision === "acceptee" ? "Commentaire (optionnel)" : "Commentaire obligatoire"}
                          value={decisionForm.commentaire} onChange={e => setDecisionForm({ ...decisionForm, commentaire: e.target.value })} data-testid={`prop-commentaire-${p.id}`} />
                        <Button size="sm" onClick={() => deciderProposition(p.id)} data-testid={`prop-decision-submit-${p.id}`} className="bg-[#1B2A4A] text-white">Confirmer</Button>
                      </div>
                    )}
                    {isGE && p.statut === "acceptee" && (
                      <Button size="sm" className="mt-3 bg-[#1B2A4A] text-white" onClick={() => preparerAffectations(p.id)} data-testid={`prop-prepare-${p.id}`}>
                        <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Préparer les affectations
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {isGE && ["transmise", "en_etude", "a_ajuster"].includes(detail.statut) && (
                <div className="border-t pt-4 space-y-3" data-testid="proposition-form">
                  <div className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Préparer une proposition (sans ressaisie)</div>
                  {(detail.lignes || []).map((l, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-xs w-48 truncate">{l.nombre}× {l.profil}</span>
                      <Select value={propForm.profils.find(p => p.ligne_profil === l.profil)?.salarie_id || "__libre__"}
                        onValueChange={v => setPropForm({ ...propForm, profils: [...propForm.profils.filter(p => p.ligne_profil !== l.profil), { ligne_profil: l.profil, salarie_id: v === "__libre__" ? "" : v }] })}>
                        <SelectTrigger className="flex-1" data-testid={`prop-salarie-${i}`}><SelectValue placeholder="Salarié (ou profil libre)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__libre__">Profil à identifier</SelectItem>
                          {salaries.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} — {s.poste || "—"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Tarif €/h</Label><Input type="number" step="0.5" value={propForm.tarif_horaire} onChange={e => setPropForm({ ...propForm, tarif_horaire: e.target.value })} data-testid="prop-tarif" /></div>
                    <div><Label className="text-xs">Validité (jours)</Label><Input type="number" value={propForm.validite_jours} onChange={e => setPropForm({ ...propForm, validite_jours: e.target.value })} /></div>
                    <div><Label className="text-xs">Frais prévus</Label><Input value={propForm.frais_prevus} onChange={e => setPropForm({ ...propForm, frais_prevus: e.target.value })} /></div>
                  </div>
                  <Button onClick={envoyerProposition} data-testid="prop-envoyer" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                    Envoyer la proposition à l'entreprise
                  </Button>
                </div>
              )}

              {conditions && conditions.conditions.length > 0 && (
                <div className="border-t pt-4" data-testid="demande-conditions">
                  <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Conditions avant démarrage (acceptation ≠ autorisation de démarrer)</div>
                  {conditions.conditions.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-1">
                      <span className={`h-2 w-2 rounded-full ${c.statut === "ok" ? "bg-emerald-500" : c.statut === "manuel" ? "bg-slate-400" : "bg-amber-500"}`} />
                      <span className="flex-1">{c.condition}{c.detail ? ` — ${c.detail}` : ""}</span>
                      <Badge variant="outline" className="text-[10px]">{c.responsable}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
